import { useState } from 'react';
import { format } from 'date-fns';
import { Form } from 'antd';
import { Text, Flex, Button } from '@keyslabor/shared/ui';
import { Input } from '@keyslabor/shared/ui/Primitives/Input';
import { Select } from '@keyslabor/shared/ui/Primitives/Select';
import { useEmployeeListOptions } from '@/hooks/api/useEmployeesList';
import { useCompanyInfoQuery } from '@/api/methods/useCompanyInfoQuery.ts';
import { useCreateLostHoursMutation } from '@/api/methods/useCreateLostHoursMutation.ts';
import useChooseDate from './modals/useChooseDate';

interface FormFields {
  employee: number;
  date: Date;
  hours: string;
}

interface Props {
  setStep?: (step: number) => void;
}

export default function CreateLostHourMobile({ setStep }: Props) {
  const [form] = Form.useForm<FormFields>();
  const [renderChooseDate, toggleChooseDate] = useChooseDate({
    setValue: form.setFieldValue,
  });
  const { data: companyInfo } = useCompanyInfoQuery();
  const employeeList = useEmployeeListOptions();

  const { mutateAsync: createLostHours } = useCreateLostHoursMutation();

  const onFinish = async (values: FormFields) => {
    await form.validateFields();
    await createLostHours({
      ...values,
      date: format(values?.date, 'yyyy-MM-dd'),
      created_by: companyInfo?.manager?.id ?? 0,
    });

    setStep?.(0);
  };

  const onFinishFailed = (errorInfo: unknown) => {
    console.error(errorInfo);
  };

  const onCancel = () => {
    form.resetFields();
    setStep?.(0);
  };

  return (
    <Flex
      width="100%"
      flexDirection="column"
      bg="#fff"
      pb="16px"
      padding="20px 20px 30px 20px"
    >
      <>
        <Text
          maxWidth={'248px'}
          fontWeight="700"
          fontSize="18px"
          lineHeight="21.09px"
          textAlign="center"
          display="flex"
          margin="0px auto 0px"
        >
          If you can’t find employee on list to add lost hours please contact
          agency
        </Text>

        <Form
          form={form}
          name="create-lost-hour-prop"
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          initialValues={{
            employee: undefined,
            date: new Date(),
            hours: '',
          }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          key="create-lost-hour-prop"
        >
          <Form.Item
            name="employee"
            style={{ width: '100%', margin: '20px 0px 10px 0px' }}
            rules={[{ required: true, message: 'Please input employee!' }]}
          >
            <Select
              height={'40px'}
              name="employee"
              label="Employee"
              placeholder={'------------'}
              color={'#20212480'}
              required
              showSearch={true}
              options={employeeList}
            />
          </Form.Item>

          <Form.Item
            name="date"
            style={{
              width: '100%',
              margin: '0px 0px 10px 0px',
            }}
            rules={[{ required: true, message: 'Please input date!' }]}
          >
            <Input
              width="100%"
              height="40px"
              name="date"
              label="Date"
              fontSize="14px"
              required
              borderColor="#7F87B6"
              isDate
              readOnly
              onClick={() => toggleChooseDate(form.getFieldValue('date'))}
            />
          </Form.Item>

          <Form.Item
            name="hours"
            style={{ width: '100%', margin: '0px 0px 10px 0px' }}
            rules={[
              { required: true, message: 'Please input hours!' },
              {
                validator: (_, value) => {
                  const [numbers, decimals] = value.split('.');
                  if (decimals?.length > 2) {
                    return Promise.reject(
                      'Only two decimal numbers are allowed'
                    );
                  }
                  return Promise.resolve();
                },
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (value <= 16) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('Ensure this value is less then or equal to 16 !')
                  );
                },
              }),
            ]}
          >
            <Input
              width="100%"
              height="40px"
              fontSize="14px"
              name="hours"
              required
              label="Hours"
              borderColor="#7F87B6"
              isNumber={true}
              inputMode="decimal"
            />
          </Form.Item>
          <Text
            fontSize="14px"
            color="#7F87B6"
            fontWeight="500"
            lineHeight="18px"
          >
            Attention! For example 2.5 hours equal to 2 hours 30 minutes, not 2
            hours 50 minutes
          </Text>
          <Flex width="100%" marginTop="auto" flexDirection="column">
            <Button type="submit" width="100%" bg="green" fontWeight="400">
              Submit
            </Button>
            <Button
              width="100%"
              bg="red"
              mt="10px"
              onClick={onCancel}
              fontWeight="400"
            >
              Cancel
            </Button>
          </Flex>
        </Form>
      </>
      {renderChooseDate}
    </Flex>
  );
}
